var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { getConfig, getFreshAuthToken, getSessionGeneration } from '@bounded-sh/core';
import { getPlatform, isVolatileStorage } from '../platform';
/** A structured non-2xx response from the developer API. */
export class BuildsApiError extends Error {
    constructor(init) {
        super(init.message);
        this.name = 'BuildsApiError';
        this.code = init.code;
        this.retryable = init.retryable;
        this.status = init.status;
        this.details = init.details;
        this.retryAfterMs = init.retryAfterMs;
    }
}
const IDEMPOTENCY_STORAGE_PREFIX = 'bounded:build:idempotency:';
function retryAfterMs(response) {
    var _a, _b;
    const value = (_b = (_a = response.headers) === null || _a === void 0 ? void 0 : _a.get) === null || _b === void 0 ? void 0 : _b.call(_a, 'Retry-After');
    if (!value)
        return undefined;
    const seconds = Number(value);
    if (Number.isFinite(seconds) && seconds >= 0)
        return Math.ceil(seconds * 1000);
    const at = Date.parse(value);
    if (!Number.isFinite(at))
        return undefined;
    return Math.max(0, at - Date.now());
}
async function responseBody(response) {
    const text = await response.text();
    if (!text)
        return {};
    try {
        return JSON.parse(text);
    }
    catch (_a) {
        return { raw: text };
    }
}
function apiError(response, body) {
    const envelope = body;
    const nested = (envelope === null || envelope === void 0 ? void 0 : envelope.error) && typeof envelope.error === 'object'
        ? envelope.error
        : undefined;
    const hasNestedDetails = !!nested && Object.prototype.hasOwnProperty.call(nested, 'details');
    const hasTopLevelDetails = !!body
        && typeof body === 'object'
        && Object.prototype.hasOwnProperty.call(body, 'details');
    const legacyCode = typeof (body === null || body === void 0 ? void 0 : body.error) === 'string' ? body.error : undefined;
    const code = typeof (nested === null || nested === void 0 ? void 0 : nested.code) === 'string'
        ? nested.code
        : legacyCode !== null && legacyCode !== void 0 ? legacyCode : `http_${response.status}`;
    const message = typeof (nested === null || nested === void 0 ? void 0 : nested.message) === 'string'
        ? nested.message
        : typeof (body === null || body === void 0 ? void 0 : body.message) === 'string'
            ? body.message
            : `Developer API request failed with HTTP ${response.status}`;
    const retryable = typeof (nested === null || nested === void 0 ? void 0 : nested.retryable) === 'boolean'
        ? nested.retryable
        : response.status === 429 || response.status >= 500;
    return new BuildsApiError(Object.assign(Object.assign({ code,
        message,
        retryable, status: response.status }, (hasNestedDetails
        ? { details: nested === null || nested === void 0 ? void 0 : nested.details }
        : hasTopLevelDetails
            ? { details: body.details }
            : {})), (response.status === 429 ? { retryAfterMs: retryAfterMs(response) } : {})));
}
/**
 * Force one token refresh for a 401 recovery, bound to the session that
 * authenticated the original attempt.
 *
 * Returns the refreshed bearer only when the retry may proceed under the SAME login;
 * otherwise `null`, so the caller surfaces the original 401 rather than replaying the
 * request under a principal that replaced the original mid-flight. Mirrors the core
 * HTTP client's `refreshAuthSessionOnce`.
 */
async function refreshDevApiTokenOnce(capturedGeneration) {
    try {
        // force: the server already rejected the stored token, so a locally-fresh-looking
        // one is not evidence of anything — require a genuinely new credential.
        const token = await getFreshAuthToken(false, { force: true });
        if (!token)
            return null;
        // A refresh preserves the generation; a re-login mints a new one. An unchanged
        // generation covers both "still the same token" and "refreshed descendant", while
        // rejecting a retry that a concurrent login would otherwise run as someone else.
        const currentGeneration = await getSessionGeneration(false);
        if (currentGeneration !== capturedGeneration)
            return null;
        return token;
    }
    catch (_a) {
        // auth_changed or a definitively rejected refresh: the retry must not proceed.
        return null;
    }
}
/** @internal Shared authenticated developer-api transport for builds and apps. */
export async function requestDevApi(path, request = {}) {
    var _a;
    const config = await getConfig();
    const base = (_a = config.devApiUrl) === null || _a === void 0 ? void 0 : _a.replace(/\/$/, '');
    if (!base) {
        throw new Error('Bounded developer API is not configured. Pass devApiUrl or a Bounded network to init().');
    }
    // Acquire the bearer through the same refresh-aware path the core HTTP client uses,
    // NOT the raw stored-token accessor. Bounded ID tokens expire after ~1h while the
    // refresh credential lives longer, so a build-only surface left open past the expiry
    // would otherwise send a stale token, take a 401 (non-retryable here), and stop
    // polling despite holding a valid refresh token. getFreshAuthToken refreshes when
    // needed and binds the result to the session generation.
    const token = await getFreshAuthToken(false);
    if (!token) {
        throw new BuildsApiError({
            code: 'auth_required',
            message: 'Sign in before calling the Bounded developer API.',
            retryable: false,
            status: 401,
        });
    }
    // Bind any 401 recovery to the session that authenticated this first attempt.
    const capturedGeneration = await getSessionGeneration(false);
    const send = (bearer) => {
        var _a;
        const hasBody = request.body !== undefined;
        return fetch(`${base}${path}`, Object.assign({ method: (_a = request.method) !== null && _a !== void 0 ? _a : 'GET', headers: Object.assign({ Authorization: `Bearer ${bearer}` }, (hasBody ? { 'Content-Type': 'application/json' } : {})) }, (hasBody ? { body: JSON.stringify(request.body) } : {})));
    };
    let response = await send(token);
    // A token that expires between the freshness read and the server reaching it is
    // rejected with 401. Force exactly one refresh bound to the captured generation and
    // retry once; a build watcher would otherwise treat the 401 as permanent.
    if (response.status === 401) {
        const refreshed = await refreshDevApiTokenOnce(capturedGeneration);
        if (refreshed)
            response = await send(refreshed);
    }
    const body = await responseBody(response);
    if (!response.ok)
        throw apiError(response, body);
    return body;
}
function intentStorageKey(intentKey) {
    return `${IDEMPOTENCY_STORAGE_PREFIX}${intentKey}`;
}
function isValidIdempotencyKey(value) {
    return typeof value === 'string' && value.length > 0 && value.length <= 256;
}
// DS3-0416: persist through the SDK platform adapter (getPlatform().sessionStorage),
// which React Native wires to a durable backend, instead of reaching for the
// browser `globalThis.sessionStorage` global directly and silently falling back
// to a module-local Map that is lost on reload / RN restart. The volatile
// in-memory fallback is no longer accepted as durable storage for a minted key.
function idempotencySessionStorage() {
    return getPlatform().sessionStorage;
}
function readPersistedIdempotencyKey(intentKey) {
    const storageKey = intentStorageKey(intentKey);
    try {
        const stored = idempotencySessionStorage().getItem(storageKey);
        if (isValidIdempotencyKey(stored))
            return stored;
    }
    catch (_a) {
        // Storage can be disabled by browser privacy policy; treat as no key.
    }
    return undefined;
}
function persistIdempotencyKey(intentKey, idempotencyKey) {
    const storageKey = intentStorageKey(intentKey);
    try {
        idempotencySessionStorage().setItem(storageKey, idempotencyKey);
    }
    catch (_a) {
        // Best-effort; durability is confirmed separately before an auto-mint is used.
    }
}
/**
 * DS3-0416: confirm a just-minted key is durably retrievable before we rely on
 * it. The store must NOT be the volatile in-memory fallback, and a read-back
 * must return the exact key. Anything else means a reload / restart would mint a
 * fresh wire key and be admitted as a duplicate cost-bearing action.
 */
function idempotencyKeyIsDurable(intentKey, idempotencyKey) {
    const store = idempotencySessionStorage();
    if (isVolatileStorage(store))
        return false;
    try {
        return store.getItem(intentStorageKey(intentKey)) === idempotencyKey;
    }
    catch (_a) {
        return false;
    }
}
function randomHex(bytes) {
    const cryptoObject = globalThis.crypto;
    if (cryptoObject === null || cryptoObject === void 0 ? void 0 : cryptoObject.getRandomValues) {
        const values = new Uint8Array(bytes);
        cryptoObject.getRandomValues(values);
        return Array.from(values, (value) => value.toString(16).padStart(2, '0')).join('');
    }
    let value = '';
    for (let index = 0; index < bytes; index += 1) {
        value += Math.floor(Math.random() * 256).toString(16).padStart(2, '0');
    }
    return value;
}
function mintIdempotencyKey() {
    const cryptoObject = globalThis.crypto;
    if (typeof (cryptoObject === null || cryptoObject === void 0 ? void 0 : cryptoObject.randomUUID) === 'function') {
        return `build_${cryptoObject.randomUUID()}`;
    }
    return `build_${Date.now().toString(36)}_${randomHex(16)}`;
}
function actionIdempotencyKey(options, action) {
    const supplied = options.idempotencyKey;
    if (supplied !== undefined) {
        if (!isValidIdempotencyKey(supplied)) {
            throw new TypeError('idempotencyKey must be between 1 and 256 characters.');
        }
        if (options.intentKey)
            persistIdempotencyKey(options.intentKey, supplied);
        return supplied;
    }
    if (!options.intentKey) {
        throw new TypeError(`intentKey is required when ${action} auto-mints an idempotency key.`);
    }
    const existing = readPersistedIdempotencyKey(options.intentKey);
    if (existing)
        return existing;
    const minted = mintIdempotencyKey();
    // This is deliberately synchronous and happens before requestDevApi can fetch.
    persistIdempotencyKey(options.intentKey, minted);
    // DS3-0416: fail closed when the key cannot be durably persisted (RN without a
    // storage adapter, or blocked browser storage). Silently returning a volatile
    // key defeats the idempotency contract: a lost-response retry after a reload /
    // restart would mint a DIFFERENT wire key and be admitted as a duplicate
    // cost-bearing build. Require the caller to supply an explicit durable key.
    if (!idempotencyKeyIsDurable(options.intentKey, minted)) {
        throw new TypeError(`${action} could not durably persist an auto-minted idempotency key: no durable session `
            + `storage is available (React Native without a platform storage adapter, or blocked browser `
            + `storage). Pass an explicit idempotencyKey stored durably by your app instead of intentKey.`);
    }
    return minted;
}
function appPath(appId) {
    return `/app/${encodeURIComponent(appId)}/builds`;
}
export async function submit(appId, options) {
    const idempotencyKey = actionIdempotencyKey(options, 'submit()');
    const { intentKey: _intentKey, idempotencyKey: _suppliedKey } = options, submission = __rest(options, ["intentKey", "idempotencyKey"]);
    return requestDevApi(appPath(appId), {
        method: 'POST',
        body: Object.assign(Object.assign({}, submission), { idempotencyKey }),
    });
}
export async function get(appId, runId) {
    return requestDevApi(`${appPath(appId)}/${encodeURIComponent(runId)}`);
}
export async function events(appId, runId, after = 0) {
    const cursor = Number.isFinite(after) ? Math.max(0, Math.floor(after)) : 0;
    return requestDevApi(`${appPath(appId)}/${encodeURIComponent(runId)}/events?after=${cursor}`);
}
export async function cancel(appId, runId) {
    return requestDevApi(`${appPath(appId)}/${encodeURIComponent(runId)}/cancel`, { method: 'POST' });
}
export async function decideGate(appId, runId, gateId, options) {
    const idempotencyKey = actionIdempotencyKey(options, 'decideGate()');
    const { intentKey: _intentKey, idempotencyKey: _suppliedKey } = options, decision = __rest(options, ["intentKey", "idempotencyKey"]);
    return requestDevApi(`${appPath(appId)}/${encodeURIComponent(runId)}/gates/${encodeURIComponent(gateId)}/decision`, { method: 'POST', body: Object.assign(Object.assign({}, decision), { idempotencyKey }) });
}
export async function refreshReviewCapability(appId, runId, gateId, options) {
    const idempotencyKey = actionIdempotencyKey(options, 'refreshReviewCapability()');
    return requestDevApi(`${appPath(appId)}/${encodeURIComponent(runId)}/gates/${encodeURIComponent(gateId)}/review-capability/refresh`, {
        method: 'POST',
        body: {
            expectedGeneration: options.expectedGeneration,
            expectedExpiresAtMs: options.expectedExpiresAtMs,
            expectedRecoveryUntilMs: options.expectedRecoveryUntilMs,
            expectedAudience: options.expectedAudience,
            expectedReviewerBindingHash: options.expectedReviewerBindingHash,
            idempotencyKey,
        },
    });
}
/**
 * Read one exact delegated-review capability and its narrow lifecycle result.
 * This does not refresh or extend the window and exposes no generic run-view
 * authority.
 */
export async function getReviewCapability(appId, runId, gateId, options) {
    return requestDevApi(`${appPath(appId)}/${encodeURIComponent(runId)}/gates/${encodeURIComponent(gateId)}/review-capability`, {
        method: 'POST',
        body: { expectedPreviewUrl: options.expectedPreviewUrl },
    });
}
export async function topup(appId, runId, options) {
    return requestDevApi(`${appPath(appId)}/${encodeURIComponent(runId)}/topup`, { method: 'POST', body: options });
}
export async function list(appId, limit = 20) {
    const requestedLimit = Number.isFinite(limit) ? Math.max(1, Math.floor(limit)) : 20;
    return requestDevApi(`${appPath(appId)}?limit=${requestedLimit}`);
}
